import { $ } from "../dom";
import {
  getWebflowCMSListPageParamKey,
  getWebflowCMSListPageParamValue,
  isWebflowCMSPaginatedList,
  WEBFLOW_CMS_LIST_PAGINATION_WRAPPER_CLASS,
} from "./webflow-cms-list";

export type LoadCMSListPaginationButton = {
  element: Element;
  index: number;
};

export type LoadCMSListPaginationButtons = {
  nextPageButton?: LoadCMSListPaginationButton;
  previousPageButton?: LoadCMSListPaginationButton;
};

export type LoadCMSListOptions = {
  getList: (body: Element) => Element;
  appendNewItems: boolean;
  appendPaginationButtons: boolean;

  onAdd?: (items: Element[], isBefore: boolean) => void;
  onPaginationButtons?: (buttons: LoadCMSListPaginationButtons) => void;
  onIdReady?: (id: string) => void;

  onPageReady?: (pageIndex: number) => void;

  forceLoad?: boolean;
  maxConcurrency?: number;
  pageFetchStaggerMs?: number;
};

export async function loadWebflowCMSList(
  list: Element,
  options: LoadCMSListOptions,
) {
  const $list = $.make(list);

  const webflowCMSList = isWebflowCMSPaginatedList(list);

  if (!webflowCMSList) {
    return false;
  }

  const {
    currentPage: initialCurrentPage,
    pageCount: initialPageCount,
    previousPageHref: initialPreviousPageHref,
    nextPageHref: initialNextPageHref,
    previousPageElement: initialPreviousPageElement,
    nextPageElement: initialNextPageElement,
  } = webflowCMSList;

  const {
    getList,
    onAdd,
    onIdReady,
    onPageReady,
    appendNewItems,
    appendPaginationButtons,
    forceLoad = false,
    maxConcurrency = 10,
    pageFetchStaggerMs = 20,
  } = options;

  const forceLoadExtra = forceLoad ? `&forceLoad=${crypto.randomUUID()}` : "";
  const safeMaxConcurrency = Math.max(1, maxConcurrency);
  const safePageFetchStaggerMs = Math.max(0, pageFetchStaggerMs);

  let hasTriggeredNextButton = !!initialNextPageElement;
  let hasTriggeredPreviousButton = !!initialPreviousPageElement;

  let listId: string | null = null;

  function resolveListId(currentHref: string, otherHref: string) {
    if (listId) {
      return;
    }

    const currentUrl = new URL(currentHref, window.location.origin);
    const otherUrl = new URL(otherHref, window.location.origin);

    for (const [key, value] of currentUrl.searchParams.entries()) {
      if (!key.endsWith("_page")) {
        continue;
      }

      if (otherUrl.searchParams.get(key) !== value) {
        listId = key.slice(0, -"_page".length);

        onIdReady?.(listId);

        return;
      }
    }
  }

  type FetchPageResult = {
    elements: Element[];
    paginationButtons: LoadCMSListPaginationButtons;
    previousPageHref: string | null;
    nextPageHref: string | null;
  };

  async function fetchPage(href: string): Promise<FetchPageResult> {
    if (listId) {
      const url = new URL(href, window.location.origin);

      const keys = [...url.searchParams.keys()];

      for (const key of keys) {
        if (
          key.endsWith("_page") &&
          key !== getWebflowCMSListPageParamKey(listId)
        ) {
          url.searchParams.delete(key);
        }
      }

      href = "?" + url.searchParams.toString();
    }

    const response = await fetch(href + forceLoadExtra);

    const rawDocument = await response.text();

    const document = new DOMParser().parseFromString(rawDocument, "text/html");

    const $newList = $.make(getList(document.body));

    const {
      previousPageHref,
      nextPageHref,
      nextPageElement,
      previousPageElement,
    } = isWebflowCMSPaginatedList($newList.first!)!;

    const elements = $newList.children.get();

    const nextPageButton = nextPageElement
      ? {
          element: nextPageElement,
          index: $.make(nextPageElement).index()!,
        }
      : undefined;
    const previousPageButton = previousPageElement
      ? {
          element: previousPageElement,
          index: $.make(previousPageElement).index()!,
        }
      : undefined;

    return {
      elements,
      paginationButtons: {
        nextPageButton,
        previousPageButton,
      },
      previousPageHref,
      nextPageHref,
    };
  }

  function insertPaginationButton(
    paginationButton: LoadCMSListPaginationButton,
  ) {
    const { element, index } = paginationButton;

    const $paginationWrapper = $list.parent.find(
      `.${WEBFLOW_CMS_LIST_PAGINATION_WRAPPER_CLASS}`,
    );

    if (index >= 0 && index < $paginationWrapper.children.length) {
      $paginationWrapper.children.eq(index).before(element);
    } else {
      $paginationWrapper.append(element);
    }
  }

  function applyPageResult(
    elements: Element[],
    paginationButtons: LoadCMSListPaginationButtons,
    isBefore: boolean,
    pageIndex: number | null,
  ) {
    if (appendNewItems) {
      if (isBefore) {
        $list.prepend(...elements);
      } else {
        $list.append(...elements);
      }
    }

    onAdd?.(elements, isBefore);

    const onPaginationButtonsEventPayload: LoadCMSListPaginationButtons = {};

    if (!hasTriggeredNextButton && paginationButtons.nextPageButton) {
      hasTriggeredNextButton = true;
      onPaginationButtonsEventPayload.nextPageButton =
        paginationButtons.nextPageButton;

      if (appendPaginationButtons) {
        insertPaginationButton(paginationButtons.nextPageButton);
      }
    }

    if (!hasTriggeredPreviousButton && paginationButtons.previousPageButton) {
      hasTriggeredPreviousButton = true;
      onPaginationButtonsEventPayload.previousPageButton =
        paginationButtons.previousPageButton;

      if (appendPaginationButtons) {
        insertPaginationButton(paginationButtons.previousPageButton);
      }
    }

    if (
      onPaginationButtonsEventPayload.nextPageButton ||
      onPaginationButtonsEventPayload.previousPageButton
    ) {
      options.onPaginationButtons?.(onPaginationButtonsEventPayload);
    }

    if (pageIndex !== null) {
      onPageReady?.(pageIndex);
    }
  }

  type QueueItem = {
    pageIndex: number | null;
    href: string;
    isBefore: boolean;
  };

  const queue: QueueItem[] = [];

  function enqueue(item: QueueItem) {
    queue.push(item);
  }

  function buildPageHref(pageIndex: number) {
    if (!listId) {
      throw new Error("List id is not resolved");
    }

    const url = new URL(window.location.href);
    url.searchParams.set(
      getWebflowCMSListPageParamKey(listId),
      getWebflowCMSListPageParamValue(pageIndex),
    );

    return "?" + url.searchParams.toString();
  }

  function wait(delayMs: number) {
    if (delayMs <= 0) {
      return Promise.resolve();
    }

    return new Promise<void>((resolve) => {
      window.setTimeout(resolve, delayMs);
    });
  }

  const loadedPages = new Set<number>();

  async function goConcurrency(currentPage: number, pageCount: number) {
    let leftPageIndex = currentPage - 1;
    let rightPageIndex = currentPage + 1;

    while (leftPageIndex >= 0 || rightPageIndex < pageCount) {
      if (leftPageIndex >= 0) {
        if (!loadedPages.has(leftPageIndex)) {
          enqueue({
            pageIndex: leftPageIndex,
            href: buildPageHref(leftPageIndex),
            isBefore: true,
          });
        }

        --leftPageIndex;
      }

      if (rightPageIndex < pageCount) {
        if (!loadedPages.has(rightPageIndex)) {
          enqueue({
            pageIndex: rightPageIndex,
            href: buildPageHref(rightPageIndex),
            isBefore: false,
          });
        }

        ++rightPageIndex;
      }
    }

    while (queue.length > 0) {
      const batch = queue.splice(0, safeMaxConcurrency);

      const beforeExpectedPageIndexes = batch
        .filter((item) => item.isBefore)
        .flatMap((item) => (item.pageIndex === null ? [] : [item.pageIndex]))
        .sort((a, b) => b - a);
      const afterExpectedPageIndexes = batch
        .filter((item) => !item.isBefore)
        .flatMap((item) => (item.pageIndex === null ? [] : [item.pageIndex]))
        .sort((a, b) => a - b);

      const pendingBeforeByPageIndex = new Map<number, FetchPageResult>();
      const pendingAfterByPageIndex = new Map<number, FetchPageResult>();

      let nextBeforeExpectedIndex = 0;
      let nextAfterExpectedIndex = 0;

      function flushBeforeInOrder() {
        while (nextBeforeExpectedIndex < beforeExpectedPageIndexes.length) {
          const pageIndex = beforeExpectedPageIndexes[nextBeforeExpectedIndex]!;
          const result = pendingBeforeByPageIndex.get(pageIndex);

          if (!result) {
            break;
          }

          pendingBeforeByPageIndex.delete(pageIndex);
          ++nextBeforeExpectedIndex;
          applyPageResult(
            result.elements,
            result.paginationButtons,
            true,
            pageIndex,
          );
        }
      }

      function flushAfterInOrder() {
        while (nextAfterExpectedIndex < afterExpectedPageIndexes.length) {
          const pageIndex = afterExpectedPageIndexes[nextAfterExpectedIndex]!;
          const result = pendingAfterByPageIndex.get(pageIndex);

          if (!result) {
            break;
          }

          pendingAfterByPageIndex.delete(pageIndex);
          ++nextAfterExpectedIndex;
          applyPageResult(
            result.elements,
            result.paginationButtons,
            false,
            pageIndex,
          );
        }
      }

      await Promise.all(
        batch.map(async (item, index) => {
          if (index > 0 && safePageFetchStaggerMs > 0) {
            await wait(index * safePageFetchStaggerMs);
          }

          const result = await fetchPage(item.href);

          if (item.pageIndex === null) {
            applyPageResult(
              result.elements,
              result.paginationButtons,
              item.isBefore,
              null,
            );

            return;
          }

          if (item.isBefore) {
            pendingBeforeByPageIndex.set(item.pageIndex, result);
            flushBeforeInOrder();
          } else {
            pendingAfterByPageIndex.set(item.pageIndex, result);
            flushAfterInOrder();
          }
        }),
      );

      flushBeforeInOrder();
      flushAfterInOrder();
    }

    return true;
  }

  if (initialPreviousPageHref) {
    enqueue({
      pageIndex: initialCurrentPage !== null ? initialCurrentPage - 1 : null,
      href: initialPreviousPageHref,
      isBefore: true,
    });
  }

  if (initialNextPageHref) {
    enqueue({
      pageIndex: initialCurrentPage !== null ? initialCurrentPage + 1 : null,
      href: initialNextPageHref,
      isBefore: false,
    });
  }

  while (queue.length > 0) {
    const current = queue.shift()!;

    const result = await fetchPage(current.href);

    applyPageResult(
      result.elements,
      result.paginationButtons,
      current.isBefore,
      current.pageIndex,
    );

    if (current.pageIndex !== null) {
      loadedPages.add(current.pageIndex);
    }

    if (current.isBefore) {
      if (result.nextPageHref) {
        resolveListId(current.href, result.nextPageHref);
      }

      if (result.previousPageHref) {
        enqueue({
          pageIndex: current.pageIndex !== null ? current.pageIndex - 1 : null,
          href: result.previousPageHref,
          isBefore: true,
        });
      }
    } else {
      if (result.previousPageHref) {
        resolveListId(result.previousPageHref, current.href);
      }

      if (result.nextPageHref) {
        enqueue({
          pageIndex: current.pageIndex !== null ? current.pageIndex + 1 : null,
          href: result.nextPageHref,
          isBefore: false,
        });
      }
    }

    if (
      listId !== null &&
      initialCurrentPage !== null &&
      initialPageCount !== null &&
      initialPageCount > 1
    ) {
      queue.length = 0;

      return goConcurrency(initialCurrentPage, initialPageCount);
    }
  }

  return true;
}
